import React from "react";
import { Box, Button, Chip, Stack, Typography, alpha } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import colors from "../../../assets/theme/colors";
import { CARE_PROFILE_SECTION_IDS } from "../../../utils/careProfileSections";
import { PROFILE_CHAPTER_TITLES } from "../../../constants/profileChapterTitles";
import { SECTION_ICON_BY_ID, getToneStyle } from "./CareProfileOverviewCards";

export const CARE_SETUP_SECTION_IDS = [
  CARE_PROFILE_SECTION_IDS.ABOUT,
  CARE_PROFILE_SECTION_IDS.MEDICATION,
  CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD,
  CARE_PROFILE_SECTION_IDS.CARE_TEAM,
  CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL,
];

const CARE_SETUP_PRESENTATION = {
  [CARE_PROFILE_SECTION_IDS.ABOUT]: {
    label: PROFILE_CHAPTER_TITLES.PERSON,
    description: "Required to get started: name, date of birth, and care focus. Optional: photo, pronouns, language, care setting, and emergency contacts. Fields with * are required.",
  },
  [CARE_PROFILE_SECTION_IDS.MEDICATION]: {
    label: PROFILE_CHAPTER_TITLES.MEDICATIONS,
    description: "Manage medication details, schedules, and verification",
  },
  [CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD]: {
    label: PROFILE_CHAPTER_TITLES.ALLERGIES,
    description: "Manage allergies, dietary restrictions, and food information",
  },
  [CARE_PROFILE_SECTION_IDS.CARE_TEAM]: {
    label: "Care team",
    description: "Manage caregivers and trusted support contacts",
  },
  [CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL]: {
    label: "Emergency information",
    description: "Manage existing emergency contacts and instructions",
  },
};

export const getCareSetupDestinations = (sections = [], canOpenCareTeam = false) => {
  const sectionsById = new Map(sections.map((section) => [section.id, section]));

  return CARE_SETUP_SECTION_IDS
    .map((id) => sectionsById.get(id))
    .filter(Boolean)
    .filter((section) => Boolean(
      section.targetStep
      || (section.id === CARE_PROFILE_SECTION_IDS.CARE_TEAM && canOpenCareTeam)
    ))
    .map((section) => ({
      ...section,
      ...CARE_SETUP_PRESENTATION[section.id],
    }));
};

export const ProfileLandingSectionHeading = ({ id, icon: Icon, title, description }) => (
  <Box sx={{ px: 0.25 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
      {Icon ? <Icon aria-hidden="true" sx={{ fontSize: 22, color: colors.brand.deep }} /> : null}
      <Typography id={id} component="h2" sx={{ fontSize: { xs: "1.2rem", sm: "1.35rem" }, fontWeight: 500, color: colors.brand.deep }}>
        {title}
      </Typography>
    </Box>
    {description ? (
      <Typography sx={{ mt: 0.25, fontSize: "0.9rem", color: colors.semantic.ink2 }}>
        {description}
      </Typography>
    ) : null}
  </Box>
);

export const CaregiverHandbookHeading = ({ personName = "this person" }) => (
  <ProfileLandingSectionHeading
    id="caregiver-handbook-heading"
    icon={MenuBookRoundedIcon}
    title="Care Guide"
    description={`What caregivers need to know about ${personName} today.`}
  />
);

export const TransitionalProfileSetupHeading = () => (
  <ProfileLandingSectionHeading
    id="additional-profile-sections-heading"
    title="Additional existing profile sections"
    description="Some existing information is still managed in the previous profile sections while CaptureEz transitions to the new Handbook."
  />
);

export const LandingNavCard = ({ icon: Icon, label, description, onClick, tone: toneKey = "default" }) => {
  const tone = getToneStyle(toneKey);

  return (
    <Button
      type="button"
      onClick={onClick}
      aria-label={`Open ${label}`}
      sx={{
        minHeight: 64,
        width: "100%",
        px: 1.2,
        py: 0.9,
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr) auto",
        gap: 1,
        alignItems: "center",
        borderRadius: "18px",
        border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
        bgcolor: colors.semantic.profileFlow.cardBg,
        color: colors.semantic.ink,
        textTransform: "none",
        textAlign: "left",
        "&:hover": { bgcolor: alpha(tone.bg, 0.3), borderColor: alpha(tone.ink, 0.34) },
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: "13px", display: "grid", placeItems: "center", bgcolor: tone.bg, color: tone.ink }}>
        {Icon ? <Icon aria-hidden="true" sx={{ fontSize: 19 }} /> : null}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.98rem", fontWeight: 500, lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography sx={{ mt: 0.18, fontSize: "0.78rem", color: colors.semantic.ink2, lineHeight: 1.35 }}>
          {description}
        </Typography>
      </Box>
      <ArrowForwardRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: tone.ink }} />
    </Button>
  );
};

export const QuickAccessItem = ({ icon: Icon, label, tone: toneKey = "default", onClick }) => {
  const tone = getToneStyle(toneKey);

  return (
    <Button
      type="button"
      onClick={onClick}
      aria-label={label}
      sx={{
        flexShrink: 0,
        flexDirection: "column",
        gap: 0.55,
        minWidth: 72,
        px: 0.5,
        py: 0.5,
        borderRadius: "16px",
        textTransform: "none",
        color: colors.semantic.ink,
        "&:hover": { bgcolor: "transparent" },
      }}
    >
      <Box sx={{ width: 52, height: 52, borderRadius: "16px", display: "grid", placeItems: "center", bgcolor: tone.bg, color: tone.ink }}>
        {Icon ? <Icon aria-hidden="true" sx={{ fontSize: 22 }} /> : null}
      </Box>
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, textAlign: "center", lineHeight: 1.2, color: colors.semantic.ink2 }}>
        {label}
      </Typography>
    </Button>
  );
};

export const QuickAccessRow = ({ items = [] }) => {
  if (!items.length) return null;

  return (
    <Box component="section" aria-labelledby="quick-access-heading">
      <Typography
        id="quick-access-heading"
        sx={{ px: 0.25, mb: 0.6, fontSize: "0.72rem", fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.semantic.ink2 }}
      >
        Quick Access
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          overflowX: "auto",
          pb: 0.25,
          mx: -0.25,
          px: 0.25,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {items.map((item) => (
          <QuickAccessItem key={item.id} icon={item.icon} label={item.label} tone={item.tone} onClick={item.onClick} />
        ))}
      </Box>
    </Box>
  );
};

export const LandingFeatureCard = ({ icon: Icon, title, description, chips = [], tone: toneKey = "default", onClick }) => {
  const tone = getToneStyle(toneKey);

  return (
    <Button
      type="button"
      onClick={onClick}
      aria-label={`Open ${title}`}
      sx={{
        width: "100%",
        p: 0,
        borderRadius: "22px",
        border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
        bgcolor: colors.semantic.profileFlow.cardBg,
        textTransform: "none",
        textAlign: "left",
        overflow: "hidden",
        boxShadow: `0 4px 12px ${alpha(colors.semantic.ink, 0.03)}`,
        "&:hover": { bgcolor: colors.semantic.profileFlow.cardBg, borderColor: alpha(tone.ink, 0.34) },
      }}
    >
      <Box sx={{ width: "100%", px: { xs: 1.3, sm: 1.5 }, py: { xs: 1.2, sm: 1.35 }, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1.1, alignItems: "flex-start" }}>
        <Box sx={{ width: 48, height: 48, borderRadius: "15px", display: "grid", placeItems: "center", bgcolor: tone.bg, color: tone.ink, flexShrink: 0 }}>
          {Icon ? <Icon aria-hidden="true" sx={{ fontSize: 22 }} /> : null}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: "1.02rem", sm: "1.08rem" }, fontWeight: 500, color: colors.semantic.ink, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.3, fontSize: "0.85rem", color: colors.semantic.ink2, lineHeight: 1.35 }}>
            {description}
          </Typography>
          {chips.length ? (
            <Box sx={{ mt: 0.85, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  size="small"
                  sx={{
                    height: 24,
                    borderRadius: 999,
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    bgcolor: alpha(tone.bg, 0.6),
                    color: tone.ink,
                    "& .MuiChip-label": { px: 1.1 },
                  }}
                />
              ))}
            </Box>
          ) : null}
        </Box>
        <ArrowForwardRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: tone.ink, mt: 0.4 }} />
      </Box>
    </Button>
  );
};

export const RecentUpdatesSection = ({ items = [], onViewAll }) => {
  if (!items.length) return null;

  return (
    <Box
      component="section"
      aria-labelledby="recent-updates-heading"
      sx={{
        px: 1,
        py: 1,
        borderRadius: "18px",
        border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
        bgcolor: alpha(colors.semantic.profileFlow.cardBg, 0.7),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.25, mb: 0.6 }}>
        <Typography
          id="recent-updates-heading"
          sx={{ fontSize: "0.72rem", fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.semantic.ink2 }}
        >
          Recent Updates
        </Typography>
        {onViewAll ? (
          <Button
            type="button"
            onClick={onViewAll}
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{ minWidth: 0, p: 0, textTransform: "none", fontWeight: 500, fontSize: "0.8rem", color: colors.brand.deep, "&:hover": { bgcolor: "transparent" } }}
          >
            View all
          </Button>
        ) : null}
      </Box>
      <Stack spacing={0.5}>
        {items.map((item) => {
          const tone = getToneStyle(item.tone);
          const ItemIcon = item.icon;
          return (
            <Box
              key={item.id}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto minmax(0, 1fr) auto",
                gap: 1,
                alignItems: "center",
                px: 1.1,
                py: 0.85,
                borderRadius: "16px",
                border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
                bgcolor: colors.semantic.profileFlow.cardBg,
              }}
            >
              <Box sx={{ width: 38, height: 38, borderRadius: "12px", display: "grid", placeItems: "center", bgcolor: tone.bg, color: tone.ink }}>
                {ItemIcon ? <ItemIcon aria-hidden="true" sx={{ fontSize: 18 }} /> : null}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: "0.9rem", fontWeight: 500, color: colors.semantic.ink }}>
                  {item.title}
                </Typography>
                {item.subtitle ? (
                  <Typography noWrap sx={{ fontSize: "0.76rem", color: colors.semantic.ink2 }}>
                    {item.subtitle}
                  </Typography>
                ) : null}
              </Box>
              <Typography sx={{ fontSize: "0.72rem", color: colors.semantic.ink2, whiteSpace: "nowrap" }}>
                {item.timeLabel}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export const CollectedInformationCard = ({
  personName = "this person",
  onClick,
}) => (
  <Button
    type="button"
    onClick={onClick}
    aria-label="Open documents and notes"
    sx={{
      width: "100%",
      minHeight: 76,
      px: { xs: 1.25, sm: 1.5 },
      py: 1.1,
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr) auto",
      gap: { xs: 1, sm: 1.25 },
      alignItems: "center",
      borderRadius: "18px",
      border: `1.5px solid ${alpha(colors.brand.deep, 0.34)}`,
      bgcolor: alpha(colors.brand.deep, 0.08),
      color: colors.semantic.ink,
      textTransform: "none",
      textAlign: "left",
      boxShadow: `0 5px 14px ${alpha(colors.brand.deep, 0.08)}`,
      "&:hover": {
        bgcolor: alpha(colors.brand.deep, 0.12),
        borderColor: alpha(colors.brand.deep, 0.46),
      },
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "13px",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(colors.brand.deep, 0.14),
        color: colors.brand.deep,
      }}
    >
      <FolderCopyOutlinedIcon aria-hidden="true" sx={{ fontSize: 24 }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: { xs: "0.98rem", sm: "1.05rem" }, fontWeight: 700, lineHeight: 1.25 }}>
        Documents &amp; notes
      </Typography>
      <Typography sx={{ mt: 0.2, fontSize: { xs: "0.76rem", sm: "0.8rem" }, color: colors.semantic.ink2, lineHeight: 1.35 }}>
        Original uploads, records, plans, and saved notes for {personName}.
      </Typography>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: colors.brand.deep }}>
      <Typography sx={{ display: { xs: "none", sm: "block" }, fontSize: "0.8rem", fontWeight: 700 }}>
        View
      </Typography>
      <ArrowForwardRoundedIcon aria-hidden="true" sx={{ fontSize: 22 }} />
    </Box>
  </Button>
);

const CareSetupDestination = ({ section, onSectionSelect, onStepChange, onOpenCareTeam }) => {
  const DestinationIcon = SECTION_ICON_BY_ID[section.id];
  const handleOpen = () => {
    if (section.id === CARE_PROFILE_SECTION_IDS.CARE_TEAM && !section.targetStep) {
      onOpenCareTeam?.();
      return;
    }
    if (onSectionSelect) {
      onSectionSelect(section);
      return;
    }
    onStepChange?.(section.targetStep);
  };

  return (
    <LandingNavCard
      icon={DestinationIcon}
      label={section.label}
      description={section.description}
      tone={section.tone}
      onClick={handleOpen}
    />
  );
};

export const CareSetupSection = ({
  sections = [],
  personName = "this person",
  onSectionSelect,
  onStepChange,
  onOpenCareTeam,
  onOpenCollectedInformation,
}) => {
  const setupSections = getCareSetupDestinations(sections, Boolean(onOpenCareTeam));

  return (
    <Box component="section" aria-labelledby="care-setup-heading">
      <Stack spacing={1}>
        <ProfileLandingSectionHeading
          id="care-setup-heading"
          icon={SettingsOutlinedIcon}
          title="Care Setup"
          description="Manage structured records and access the right care tools."
        />
        {setupSections.map((section) => (
          <CareSetupDestination
            key={section.id}
            section={section}
            onSectionSelect={onSectionSelect}
            onStepChange={onStepChange}
            onOpenCareTeam={onOpenCareTeam}
          />
        ))}
        {onOpenCollectedInformation ? (
          <LandingNavCard
            icon={Inventory2OutlinedIcon}
            label={`Everything collected about ${personName}`}
            description="Notes, documents, photos, recordings, emails, and other information collected over time."
            tone="health"
            onClick={onOpenCollectedInformation}
          />
        ) : null}
      </Stack>
    </Box>
  );
};
